import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import AnimatedBackground from '../components/AnimatedBackground';
import { fireEvent } from '../utils/funnelEvents';
import ArticleStructuredData from '../components/ArticleStructuredData';
import VerticalHeader from '../components/VerticalHeader';

import AccountantsHero from './accountants/AccountantsHero';
import Workflow from './accountants/Workflow';
import PainPoints from './accountants/PainPoints';
import FilingPatterns from './accountants/FilingPatterns';
import YearEndArchive from './accountants/YearEndArchive';
import RelatedReading from './accountants/RelatedReading';
import VerticalPricing from './shared/VerticalPricing';
import AccountantsFAQ from './accountants/AccountantsFAQ';
import VerticalContact from './shared/VerticalContact';
import ResearchInviteCard from '../components/research/ResearchInviteCard';

const PAGE_TITLE = 'AI File Organizer for Accountants and CPAs | Sortio';
const PAGE_DESCRIPTION =
  'Sortio automates accounting file organization for tax season. Auto-rename receipt and invoice PDFs, route 1099s, K-1s, and W-2s by client and tax year, prep year-end archives. Built for solo CPAs, EAs, and bookkeeping shops. BYOK or fully local AI options, runs on Mac, Windows, and Linux.';

const NAV = [
  { href: '#workflow', label: 'How it works' },
  { href: '#patterns', label: 'Filing patterns' },
  { href: '#year-end', label: 'Year-end' },
  { href: '#pricing', label: 'Pricing' },
];

const AccountantsPage = () => {
  useEffect(() => {
    fireEvent('team_page_view', { vertical: 'accountants' });
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          name="keywords"
          content="ai file organizer for accountants, automate accounting file organization, auto rename receipts CPA, tax season file management, 1099 routing software, client folder bookkeeping, year-end archive prep, CPA workflow automation"
        />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.getsortio.com/for-accountants" />
        <link rel="canonical" href="https://www.getsortio.com/for-accountants" />
      </Helmet>
      <ArticleStructuredData
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        datePublished="2026-05-14T00:00:00.000Z"
        dateModified="2026-05-14T00:00:00.000Z"
        authorName="Sortio Team"
      />

      <VerticalHeader verticalLabel="Accountants" navItems={NAV} />
      <AccountantsHero />

      <div className="relative">
        <AnimatedBackground variant="business" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <Workflow />
          <PainPoints />
          <FilingPatterns />
          <YearEndArchive />
          <RelatedReading />
          <VerticalPricing
            verticalLabel="Accountants"
            description="Most solo CPAs and EAs start with Pro. Bookkeeping shops with multiple preparers graduate to Team for a shared rule library across the client base."
            proDescription="For solo CPAs, EAs, and tax preparers"
            proFeatureLabel="Auto-rename receipts and 1099 routing"
          />
          <AccountantsFAQ />
          <ResearchInviteCard vertical="accounting" />
          <VerticalContact
            waitlistType="accountants"
            body="Tell us about your practice and we will show you Sortio working with a sample of your client folders."
            orgLabel="Firm Name"
            orgPlaceholder="Smith CPA PLLC"
            sizeLabel="Firm Size"
          />
        </div>
      </div>
    </>
  );
};

export default AccountantsPage;
